import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Card, Table, Button, Input, notification, Modal, Select, Typography } from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { getPermissions, hasPermission } from '../../components/permissions';

const { confirm } = Modal;
const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;

const columns = [
    {
        title: "#",
        dataIndex: "key",
        key: "key"
    },
    {
        title: "First Name",
        dataIndex: "firstName",
        key: "firstName",
        sorter: true,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search First Name`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={confirm}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Button
                    type="primary"
                    onClick={confirm}
                    icon={<SearchOutlined />}
                    size="small"
                    style={{ width: 90, marginRight: 8 }}
                >
                    Search
                </Button>
                <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
                    Reset
                </Button>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) => record?.firstName?.toString().toLowerCase().includes(value.toLowerCase()),
    },
    {
        title: "Last Name",
        dataIndex: "lastName",
        key: "lastName",
        sorter: true,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search Last Name`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={confirm}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Button
                    type="primary"
                    onClick={confirm}
                    icon={<SearchOutlined />}
                    size="small"
                    style={{ width: 90, marginRight: 8 }}
                >
                    Search
                </Button>
                <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
                    Reset
                </Button>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) => record?.lastName?.toString().toLowerCase().includes(value.toLowerCase()),
    },
    {
        title: "Role",
        key: 'roleName',
        dataIndex: 'roleName', 
        onFilter: (value, record) => record.userType === value,
        render: (roleName, record) => (
            <div className="author-info">
                <Title level={5}>{roleName}</Title>
                 <p>{record.description}</p>  
            </div>
        ),
    },
    {
        title: "Email",
        dataIndex: "email",
        key: "email",
        sorter: true,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search Email`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={confirm}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Button
                    type="primary"
                    onClick={confirm}
                    icon={<SearchOutlined />}
                    size="small"
                    style={{ width: 90, marginRight: 8 }}
                >
                    Search
                </Button>
                <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
                    Reset
                </Button>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) => record?.email?.toString().toLowerCase().includes(value.toLowerCase()),
    },
    {
        title: "Phone Number",
        dataIndex: "phoneNo",
        key: "phoneNo",
        sorter: true,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search Phone Number`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={confirm}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Button
                    type="primary"
                    onClick={confirm}
                    icon={<SearchOutlined />}
                    size="small"
                    style={{ width: 90, marginRight: 8 }}
                >
                    Search
                </Button>
                <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
                    Reset
                </Button>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) => record?.phoneNo?.toString().toLowerCase().includes(value.toLowerCase()),
    },
    {
        title: "Status",
        key: "status",
        dataIndex: "status",
        filters: [
            { text: 'Active', value: 'Active' },
            { text: 'Inactive', value: 'Inactive' }
        ],
        onFilter: (value, record) => record.status === value,
        render: (status) => (
            <span className={status === 'Active' ? 'text-success' : 'text-danger'}>
                {status === 'Active' ? 'Active' : 'Inactive'}
            </span>
        ),
    },
    {
        title: "Action",
        key: "action",
        dataIndex: "action",
    }
];

function StaffList() {
    const [userList, setUserList] = useState([]);
    const [pagination, setPagination] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({
        name: '',
        description: '',
        status: ''
    });

    useEffect(() => {
        getUserList();
    }, []);

    async function getUserList(page = 1, limit = 10, sortField = null, sortOrder = null, search = '', filters = {}) {
        try {
            setCurrentPage(page);
            setLimit(limit);
            const sortParam = sortField && sortOrder ? `&sortField=${sortField}&sortOrder=${sortOrder}` : '';
            const searchParam = search ? `&search=${search}` : '';
            const filterParams = Object.keys(filters)
                .map(key => `${key}=${filters[key]}`)
                .join('&');
            const response = await axios.get(`${baseUrl}/staff/list?page=${page}&limit=${limit}${sortParam}${searchParam}&${filterParams}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                }
            });
            if (response.data.status) {
                setUserList(response.data.result);
                setPagination({
                    current: page,
                    pageSize: limit,
                    total: response.data.totalCount
                });
            } else {
                notification.info({
                    message: 'Info',
                    description: response.data.message,
                    placement: 'topRight'
                });
            }
        } catch (error) {
            console.error("API error:", error);
            notification.info({
                message: 'Info',
                description: error.response?.data?.message,
                placement: 'topRight'
            });
        }
    };

    async function handleDelete(id) {
        try {
            const response = await axios.delete(`${baseUrl}/staff/delete/${id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                },
            });
            if (response.status === 200) {
                getUserList(pagination.current, pagination.pageSize, sortField, sortOrder, search, filters);
                notification.success({
                    message: 'Success',
                    description: 'Staff deleted successfully!',
                    placement: 'topRight'
                });
            } else {
                notification.info({
                    message: 'Info',
                    description: response.data.message,
                    placement: 'topRight'
                });
            }
        } catch (error) {
            console.error("API error:", error);
            notification.info({
                message: 'Info',
                description: error.response?.data?.message,
                placement: 'topRight'
            });
        }
    };

    const showDeleteConfirm = (id) => {
        confirm({
            title: 'Are you sure you want to delete this item?',
            icon: <ExclamationCircleOutlined />,
            content: 'This action cannot be undone.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk() {
                handleDelete(id);
            },
        });
    };

    const handleTableChange = (pagination, filters, sorter) => {
        const { current, pageSize } = pagination;
        const { field, order } = sorter;
        const formattedOrder = order === 'ascend' ? 'asc' : 'desc';
        setSortField(field);
        setSortOrder(formattedOrder);
        setFilters(filters);
        getUserList(current, pageSize, field, formattedOrder, search, filters);
    };

    const handleSearch = value => {
        setSearch(value);
        getUserList(currentPage, limit, sortField, sortOrder, value, filters);
    };

    const handleLimitChange = value => {
        setLimit(value);
        getUserList(1, value, sortField, sortOrder, search, filters);
    };

    return (
        <div className="tabled">
            <Row gutter={[24, 0]}>
                <Col xs="24" xl={24}>
                    <Card
                        bordered={false}
                        className="criclebox tablespace mb-24"
                        title="Staff"
                        extra={hasPermission('/staff', 'createPermission') && (<Link className="custom-btn" to="/staff/add">Add</Link>)}
                    >
                        <div className="table-responsive">
                            <Table
                                title={() => (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <span style={{ marginRight: 10 }}>Records per page:</span>
                                            <Select defaultValue={10} onChange={handleLimitChange}>
                                                <Option value={10}>10</Option>
                                                <Option value={20}>20</Option>
                                                <Option value={30}>30</Option>
                                                <Option value={40}>40</Option>
                                            </Select>
                                        </div>
                                        <Search
                                            placeholder="Search by first name, last name or email"
                                            onSearch={handleSearch}
                                            enterButton={<SearchOutlined />}
                                            style={{ width: 300 }}
                                        />
                                    </div>
                                )}
                                columns={columns}
                                dataSource={userList.map((user, index) => ({
                                    key: (limit * (currentPage - 1)) + index + 1,
                                    firstName: user.firstName,
                                    lastName: user.lastName,
                                    userType: user.userType,
                                    roleName: user.roleName, // Add this line
                                    email: user.email,
                                    phoneNo: user.phoneNo,
                                    status: user.status,
                                    action: (
                                        <div className="button-container">
                                            {hasPermission('/staff', 'editPermission') && (
                                                <Link to={`/staff/update/${user.id}`} className="update-btn">
                                                    <EditOutlined />
                                                </Link>
                                            )}
                                            {hasPermission('/staff', 'deletePermission') && (
                                                <button onClick={() => showDeleteConfirm(user.id)} className="delete-btn">
                                                    <DeleteOutlined />
                                                </button>
                                            )}
                                        </div>
                                    )
                                }))}
                                pagination={{
                                    ...pagination,
                                    showTotal: total => `Total ${total} records`,
                                    style: { marginRight: 20 }
                                }}
                                onChange={handleTableChange}
                                className="ant-border-space"
                            />
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default StaffList;
